import { FC } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import Logger from 'utils/logger';
import PostTimeSaversApi, {
  SendForApprovalParams,
} from 'app/api/postTimeSavers';
import { useToast } from 'app/hooks/useToast';
import { queryKey } from 'constants/queryKey';
import { ApiClientError } from 'api/client';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { UserActions } from 'modules/user/userActions';
import { FirstTimeFlagKey } from 'types/User';
import { useUser } from 'modules/user/userSelector';
import { getUserFirstTimeFlagStatus } from 'utils/user/firstTimeFlags';
import { useFirstApprovalRequestedModal } from 'app/components/modals/firstTimeFlags/firstApprovalRequested/FirstApprovalRequestedModal';
import {
  hasPostSentForApproval,
  markPostSentForApproval,
} from 'app/modules/onboardingChecklist/utils/sentForApprovalStorage';

type Params = {
  submitSendForApproval: (params: Omit<SendForApprovalParams, 'type'>) => void;
  isLoading: boolean;
};

type Props = {
  children: (params: Params) => JSX.Element;
  successCallback?: () => void;
  type: ApprovalType;
};

const RequestContainer: FC<Props> = ({ children, successCallback, type }) => {
  const toast = useToast();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const user = useUser();
  const openFirstApprovalRequestedModal = useFirstApprovalRequestedModal(
    (state) => state.open,
  );
  const { mutate: mutateSendForApproval, isLoading: isSendForApprovalLoading } =
    useMutation(PostTimeSaversApi.sendForApproval, {
      onSuccess: () => {
        const isFirstClientApprovalRequest =
          type === 'client' &&
          !hasPostSentForApproval(user.id, 'client') &&
          getUserFirstTimeFlagStatus(
            user.firstTimeFlags,
            FirstTimeFlagKey.FIRST_CLIENT_APPROVAL_REQUESTED,
          ) === false;

        queryClient.invalidateQueries(queryKey.dashboardPosts());
        queryClient.invalidateQueries(queryKey.postRevisions());
        queryClient.invalidateQueries(queryKey.postsMeta());
        dispatch(
          UserActions.setFirstTimeFlag(
            type === 'client'
              ? FirstTimeFlagKey.FIRST_CLIENT_APPROVAL_REQUESTED
              : FirstTimeFlagKey.FIRST_INTERNAL_APPROVAL_REQUESTED,
          ),
        );
        markPostSentForApproval(user.id, type);
        successCallback?.();

        if (isFirstClientApprovalRequest) {
          openFirstApprovalRequestedModal();
        }
      },
      onError: (error: ApiClientError) => {
        toast(error?.userMessage ?? t('somethingWentWrongTryAgain'), 'error');
        Logger.error(error);
      },
    });

  const handleSubmitSendForApproval = (
    formValues: Omit<SendForApprovalParams, 'type'>,
  ) => {
    mutateSendForApproval({ type, ...formValues });
  };

  return children({
    submitSendForApproval: handleSubmitSendForApproval,
    isLoading: isSendForApprovalLoading,
  });
};

export default RequestContainer;
